/* eslint-disable indent */
import React, { useEffect } from 'react';
import { CustomToolbar } from './components/header/CustomToolbar';
import i18n from '../../../../i18';
import { useNavigate, useParams } from 'react-router-dom';
import { DirectInboxLinearIcon, TrashLinearIcon } from '../../../../icons-lib';
import { Box } from '@mui/material';
import CustomAccordion from './components/main/CustomAccordion';
import { CampaignDataAdapter } from './components/services/CampaignsDataAdapter';
import { getCampaignById } from './components/services/fetches';
import { CampaignFormFields } from './components/services/CampaignFormFields';
import CampaignsFields from './components/forms/fields';

function MarketingCampaignsEditor(props) {
    // get params from useParams
    const accordionRef = React.useRef(null);
    const { id } = useParams();
    const [data, setData] = React.useState({
        imageId: null,
        imageUrl: null,
        campaignName: '',
        languages: ['en', 'de', 'fr', 'it'],
        notificationDetails: {
            en: { title: '', text: '' },
            de: { title: '', text: '' },
            fr: { title: '', text: '' },
            it: { title: '', text: '' }
        },
        status: 'draft',
        users: [],
        displayEndDate: 'no',
        selectedAudiences: 'selectedAudience', // By default - selected audiences
        audiences: [],
        locationId: null,
        notificationTiming: 'sendNow',
        scheduledDate: null,
        campaignEndDate: null,
        deepLink: null
    });
    // useContext
    const server = props.context.getState().server.marketingCampaigns;
    const navigate = useNavigate();
    useEffect(() => {
        const fetchData = async () => {
            if (!id) {
                return;
            }

            try {
                const apiResponse = await getCampaignById(id, server);

                if (apiResponse) {
                    const transformedData =
                        CampaignDataAdapter.transformForUi(apiResponse);
                    setData(transformedData);
                } else {
                    // Show error to user
                    props.context.update({
                        alertError: {
                            message: i18n.t('messages.campaignLoadError'),
                            type: 'snackbar',
                            status: 'error'
                        }
                    });
                }
            } catch (error) {
                props.context.update({
                    alertError: {
                        message: i18n.t('messages.campaignLoadError'),
                        type: 'snackbar',
                        status: 'error'
                    }
                });
            }
        };

        fetchData();
    }, [id, server]);

    const handleCancel = () => {
        navigate('/app/marketing-campaigns');
    };
    const handleSend = async (status) => {
        try {
            let preparedData = CampaignDataAdapter.prepareForRequest(data);
            // Convert status to correct enum format
            preparedData.status = status.toUpperCase();

            let response;
            if (id) {
                // Update existing campaign
                response = await server.update(id, preparedData);
            } else {
                // Create new campaign
                response = await server.add(preparedData);
            }

            if (response.error === 'OK') {
                // Successful save

                // Show success notification
                const successMessage =
                    status.toUpperCase() === 'SENT'
                        ? i18n.t('messages.campaignSentSuccess')
                        : i18n.t('messages.campaignSavedSuccess');

                props.context.update({
                    alertError: {
                        message: successMessage,
                        type: 'snackbar',
                        status: 'success'
                    }
                });

                // Redirect to campaign list page
                navigate('/app/marketing-campaigns');
            } else {
                // Error on send
                const errorMessage =
                    response.error || i18n.t('messages.campaignSaveError');

                props.context.update({
                    alertError: {
                        message: errorMessage,
                        type: 'snackbar',
                        status: 'error'
                    }
                });
            }
        } catch (error) {
            props.context.update({
                alertError: {
                    message: i18n.t('messages.campaignSaveError'),
                    type: 'snackbar',
                    status: 'error'
                }
            });
        }
    };

    // onChange
    const handleChange = (field, value) => {
        setData((prevData) => {
            const newData = {
                ...prevData,
                [field]: value
            };
            return newData;
        });
    };

    // ui functions
    const headerTitle = id
        ? i18n.t(`pages.marketingCampaigns.edit`)
        : i18n.t(`pages.marketingCampaigns.add`);

    const [expanded, setExpanded] = React.useState(null);
    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    return (
        <div>
            <CustomToolbar
                title={headerTitle}
                handleBack={handleCancel}
                actions={[
                    {
                        title: i18n.t('buttons.saveAsDraft'),
                        onClick: () => handleSend('draft'),
                        icon: <DirectInboxLinearIcon />
                    },
                    {
                        title: i18n.t('buttons.discard'),
                        onClick: () => handleCancel(),
                        icon: <TrashLinearIcon />
                    }
                ]}
            />
            <Box
                display={'flex'}
                alignItems={'center'}
                flexDirection={'column'}
            >
                {/* form */}
                {CampaignFormFields.map((group, index) => (
                    <CustomAccordion
                        ref={accordionRef}
                        key={index}
                        title={i18n.t(`campaigns.${group.title}`)}
                        expanded={expanded === `panel${index}`}
                        onChange={handleAccordionChange(`panel${index}`)}
                        icon={group.icon}
                        actions={[
                            group.endOfForm
                                ? {
                                      title: i18n.t('buttons.send'),
                                      onClick: () => handleSend('sent')
                                  }
                                : {
                                      title: i18n.t('buttons.next'),
                                      onClick: () =>
                                          setExpanded(`panel${index + 1}`)
                                  }
                        ]}
                    >
                        {group.fields.map((field, i) => {
                            let Field = CampaignsFields[field.type];
                            if (!Field) Field = CampaignsFields.text;

                            // Conditional rendering for campaignEndDate
                            if (field.id === 'campaignEndDate') {
                                if (data.displayEndDate !== 'yes') {
                                    return null;
                                }
                            }

                            // Conditional rendering for scheduledDate
                            if (field.id === 'scheduledDate') {
                                if (data.notificationTiming !== 'sendLater') {
                                    return null;
                                }
                            }

                            return (
                                <Field
                                    key={field.id}
                                    field={field}
                                    data={data[field.id] || null}
                                    additionalData={
                                        field.anotherFieldData
                                            ? data[field.anotherFieldData]
                                            : null
                                    }
                                    onChange={(fieldId, value) =>
                                        handleChange(fieldId, value)
                                    }
                                    context={props.context}
                                    wrapperRef={accordionRef}
                                />
                            );
                        })}
                    </CustomAccordion>
                ))}
            </Box>
        </div>
    );
}

export default MarketingCampaignsEditor;
